/**
 * One plat model, drawn onto a page.
 *
 * The counterpart of the SVG in PlatMap: same model, same order, same weights,
 * different surface. What it adds is everything a printed page needs and a web
 * page does not — a decision about where the map stops and the notes under it
 * begin, and how big the map can be once they have had their room.
 *
 * The page it is drawn on — border, running bars, footer stamp — is not this
 * file's business; it is pdf/page.ts, shared with the incident packet so the
 * two documents come out of the same printer looking like one system.
 *
 * The map goes on its own page. The roster of households follows on its own,
 * because a walking map shrunk to make room for a list of names is worse at
 * both jobs than two sheets that are each good at one.
 */
import {
  HATCH_LINE, HATCH_OPACITY, HATCH_PX,
  LOT_LINE, OUTSIDE_GRAY,
  MIN_LABEL_PX, STREET_LABEL_PX,
  WARD_BOUNDARY,
  type PlatModel, type Seg, type XY,
} from './model';
import { PT_PER_PX, textWidth, type PdfDoc, type PdfPage, type Pt } from '../pdf/pdf';
import {
  clip, contentBox, furniture, pageSize, type Box,
} from '../pdf/page';

const NOTE_PT = 6.5;
const NOTE_LEAD = 8;
const ROSTER_PT = 8;
const ROSTER_LEAD = 10.5;

export interface SheetFlags {
  showShading: boolean;
  showNames: boolean;
}

export { contentBox, pageSize };

/** Height the notes under the map will need, so the map can be fitted above them. */
export function noteHeight(model: PlatModel, _flags: SheetFlags, boxW: number): number {
  let h = 0;
  if (model.listed.length) {
    h += 6 + Math.ceil(model.listed.length / noteColumns(boxW)) * NOTE_LEAD;
  }
  // Nothing else goes under the map any more; the unplaceable households moved
  // to the roster, which gives the drawing back the room they took.
  return h;
}

const noteColumns = (boxW: number) => (boxW > 600 ? 4 : 3);

/** Largest the map can be drawn in the space left, and where it sits. */
export function fitMap(frame: Box, box: Box, notesH: number) {
  const availW = box.x1 - box.x0;
  const availH = box.y1 - box.y0 - (notesH ? notesH + 8 : 0);
  const fw = frame.x1 - frame.x0, fh = frame.y1 - frame.y0;
  const scale = Math.min(availW / fw, availH / fh);
  const w = fw * scale, h = fh * scale;
  // The map and its notes are centred as one block. A map is rarely the shape
  // of a page — the whole ward is three and a half times wider than it is tall —
  // so there is always slack on one axis. Split evenly it reads as a mounted
  // drawing; all of it below the map reads as a page that failed to fill.
  const group = h + (notesH ? notesH + 8 : 0);
  const top = box.y1 - Math.max(0, (box.y1 - box.y0 - group) / 2);
  return {
    scale, w, h,
    x: box.x0 + (availW - w) / 2,
    y: top - h,
  };
}

export interface MapPageOptions {
  title: string;
  subtitle: string;
  flags: SheetFlags;
  /**
   * A line between the head bar and the map — who is walking this sector, on
   * what channel, when they started.
   *
   * The map page is the one a walker actually carries, so the answers they
   * need at the door belong on it rather than on a roster page that stays on
   * the clipboard. The map is fitted to what is left, so the drawing shrinks
   * by the height of the line rather than being drawn over.
   */
  subhead?: (page: PdfPage, box: Box) => number;
}

export function drawMapPage(page: PdfPage, model: PlatModel, o: MapPageOptions) {
  furniture(page, o.title, o.subtitle);
  let box = contentBox(page.w, page.h);
  if (o.subhead) box = { ...box, y1: o.subhead(page, box) };
  const notesH = noteHeight(model, o.flags, box.x1 - box.x0);
  const fit = fitMap(model.frame, box, notesH);

  const X = (gx: number) => fit.x + (gx - model.frame.x0) * fit.scale;
  // Grid south increases downward and PDF y increases upward, so this is where
  // the flip happens — once, in the coordinate map, rather than as a matrix
  // that would mirror every piece of text drawn under it.
  const Y = (gy: number) => fit.y + (model.frame.y1 - gy) * fit.scale;
  const P = (p: XY): Pt => ({ x: X(p.x), y: Y(p.y) });
  const S = (segs: Seg[]): [Pt, Pt][] => segs.map(([a, b]) => [P(a), P(b)]);

  page.save();
  // Roads and boundaries run past the frame; the SVG's viewBox cropped them.
  page.clipRect(fit.x, fit.y, fit.w, fit.h);

  // Every parcel, so a walker can see the lots either side of theirs. A lot
  // that is neither shaded nor outlined is not drawn at all — it would be
  // white on white, and there are sixteen hundred of them.
  //
  // Outside first, ALL of it, so a sector lot's own line is never overdrawn
  // by a paler neighbour sharing the same edge. That has to include the
  // neighbour's merged outline, not only its lot lines: a grouped property
  // has no lot lines of its own, so the merged outline is its only edge, and
  // drawing every merged outline after every lot line greyed out whatever
  // stretch a sector lot happened to share with one. 3328 S 500 E lost most
  // of its west edge that way.
  let lw = -1, fg = -1, sg = -1;
  const lots = (pass: boolean) => {
    const gray = pass ? 0 : OUTSIDE_GRAY;
    if (gray !== sg) { page.strokeGray(gray); sg = gray; }
    for (const p of model.parcels) {
      if (p.inSector !== pass) continue;
      const fill = o.flags.showShading && p.filled;
      if (!fill && !p.outlined) continue;
      const width = LOT_LINE * PT_PER_PX;
      if (p.outlined && width !== lw) { page.lineWidth(width); lw = width; }
      if (fill && fg !== 0.85) { page.fillGray(0.85); fg = 0.85; }
      const mode = fill && p.outlined ? 'B' : fill ? 'f' : 'S';
      for (const ring of p.rings) page.poly(ring.map(P), true, mode);
    }
  };
  // A property's merged outline, drawn over the lots it was made from — and
  // fading with them, since this is the only edge a grouped parcel has.
  const properties = (pass: boolean) => {
    const gray = pass ? 0 : OUTSIDE_GRAY;
    if (gray !== sg) { page.strokeGray(gray); sg = gray; }
    if (lw !== LOT_LINE * PT_PER_PX) { page.lineWidth(LOT_LINE * PT_PER_PX); lw = LOT_LINE * PT_PER_PX; }
    for (const m of model.merged) if (m.inSector === pass) page.segments(S(m.d));
  };

  lots(false);
  properties(false);
  lots(true);
  page.strokeGray(0); sg = 0;

  // Multi-unit buildings, hatched over the fill and under the lot lines.
  //
  // The lattice is laid out in page coordinates and each shape clipped to it,
  // rather than each shape hatched from its own corner, so the stripes run
  // straight across a property the county maps as a hundred polygons instead
  // of stepping at every lot line.
  //
  // There is no alpha here — the writer emulates opacity by lightening — so
  // the grey is worked out per parcel from what it is drawn on. Both come out
  // as the same colour the SVG composites to.
  const hatchStep = HATCH_PX * PT_PER_PX * Math.SQRT2;
  for (const p of model.parcels) {
    if (!p.striped) continue;
    const rings = p.rings.map((r) => r.map(P));
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const r of rings) for (const q of r) {
      x0 = Math.min(x0, q.x); x1 = Math.max(x1, q.x);
      y0 = Math.min(y0, q.y); y1 = Math.max(y1, q.y);
    }
    if (!(x1 > x0 && y1 > y0)) continue;
    // Lines of x + y = c, which run top-left to bottom-right on the page.
    // `c` is absolute, so the phase is shared by every shape on the sheet.
    const lines: [Pt, Pt][] = [];
    const from = Math.ceil((x0 + y0) / hatchStep) * hatchStep;
    for (let c = from; c <= x1 + y1; c += hatchStep) {
      lines.push([{ x: c - y0, y: y0 }, { x: c - y1, y: y1 }]);
    }
    if (!lines.length) continue;
    page.save();
    page.clipPoly(rings);
    const under = o.flags.showShading && p.filled ? 0.85 : 1;
    // A neighbour's apartment block fades with the rest of its lot. Left at
    // full strength the hatch is the loudest thing on the sheet, and it would
    // be shouting about a building the walker is not calling on.
    const ink = p.inSector ? HATCH_OPACITY : HATCH_OPACITY * 0.35;
    page.strokeGray(1 - (1 - under) - ink * under);
    page.lineWidth(HATCH_LINE * PT_PER_PX);
    page.segments(lines);
    page.restore();
  }

  // The in-sector half of the merged outlines, after the hatch like the lot
  // lines above it. The outside half was drawn before them; see `properties`.
  properties(true);
  page.strokeGray(0); sg = 0;

  page.strokeGray(0.65);
  page.lineWidth(0.4 * PT_PER_PX);
  page.dash(3 * PT_PER_PX, 3 * PT_PER_PX);
  for (const r of model.roads) page.poly(r.pts.map(P), false, 'S');
  page.dash(0, 0);

  // One name per street, turned to follow it but never upside down. The sign
  // of the angle flips with the y axis.
  for (const l of model.streetLabels) {
    page.text(l.name, X(l.x), Y(l.y), {
      size: STREET_LABEL_PX * PT_PER_PX,
      align: 'center',
      rotate: -l.deg,
      halo: 1.6 * PT_PER_PX,
    });
  }

  // The ward edge, heavy enough to read at arm's length, and the only
  // boundary on the sheet.
  page.strokeGray(0);
  page.lineWidth(3 * PT_PER_PX);
  page.lineJoinRound();
  page.poly(WARD_BOUNDARY.map(P), true, 'S');

  // No dots. A household with no county parcel used to be drawn at its
  // geocode, which on a named street is interpolated along the block and can
  // be most of a block out — a mark on the map that looks as placed as a
  // shaded lot and is not. They are listed by address on the roster instead,
  // under "Not on the map", where a walker can read what they are looking for.

  for (const l of model.labels) {
    page.text(l.text, X(l.x), Y(l.y),
              { size: MIN_LABEL_PX * PT_PER_PX, bold: l.bold, align: 'center',
                halo: l.halo ? 1.2 * PT_PER_PX : 0 });
  }
  page.restore();

  page.strokeGray(0);
  page.lineWidth(0.5);
  page.dash(0, 0);
  page.rect(fit.x, fit.y, fit.w, fit.h, 'S');

  // Notes below the map: the key to the numbered lots, then what could not be
  // drawn. All three belong to the drawing, not to the roster — a numbered
  // index is meaningless on a different page from its numbers.
  let y = fit.y - 8 - NOTE_PT;
  const cols = noteColumns(box.x1 - box.x0);
  const colW = (box.x1 - box.x0) / cols;

  if (model.listed.length) {
    const rows = Math.ceil(model.listed.length / cols);
    model.listed.forEach((l, i) => {
      const cx = box.x0 + Math.floor(i / rows) * colW;
      const cy = y - (i % rows) * NOTE_LEAD;
      page.text(String(l.n), cx, cy, { size: NOTE_PT, bold: true });
      const off = textWidth(`${l.n} `, NOTE_PT, true);
      page.text(clip(l.address, colW - off - 6, NOTE_PT), cx + off, cy, { size: NOTE_PT });
    });
    y -= rows * NOTE_LEAD + 6;
  }

  // The unplaceable households used to be noted here, under the drawing. They
  // are on the roster now: this space is for the key to the numbered lots,
  // which only makes sense beside the numbers it explains.
}

export interface RosterRow { key: string; unit?: string | null; label: string }

/**
 * The address as a person reads it, which is not the address the map joins on.
 *
 * `key` is the street line, and that is deliberately all it is: it joins a
 * household to a county parcel, and the county maps a building rather than
 * its doors. Twelve households at 1156 E 3300 S share one key. A list of
 * households has to tell them apart, so the unit goes back on here.
 */
const rosterAddress = (r: RosterRow) => (r.unit ? `${r.key} ${r.unit}` : r.key);

/** Head of each roster column, and the gap between a column and the next. */
const ROSTER_HEAD_PT = 6;
const ROSTER_GUTTER = 12;
/** Space between the address column and the name beside it. */
const ROSTER_GAP = 8;

/**
 * The households on the sheet, as many pages as it takes.
 *
 * A table, not a list: the address column is one width for the whole document,
 * so every name starts at the same place down the column and across the page.
 * Set from each address's own width the names stepped in and out row by row,
 * and the eye had to find the start of each one instead of running down them.
 *
 * The width is the widest address in the roster, capped, rather than a fixed
 * measure — a walk of short numbers should not carry a column sized for the
 * ward's longest address.
 *
 * Portrait regardless of which way the map turned: this is a column of text,
 * and a column of text wants the tall page.
 */
/**
 * A household the map could not place, and why in one word.
 *
 * `dot` was the old answer: draw it at its geocode and hope. A geocode on a
 * named street is interpolated along the block, so the mark could be most of
 * a block from the door while looking exactly as placed as a shaded lot.
 * Better to say plainly that the address is not on the drawing.
 */
export interface UnmappedRow extends RosterRow {
  /** 'no-parcel' — inside the ward, nothing in the county matched the address.
   *  'outside'   — the address geocodes beyond the ward boundary. */
  why: 'no-parcel' | 'outside';
}

export function drawRosterPages(
  doc: PdfDoc, title: string, rows: RosterRow[], unmapped: UnmappedRow[] = [],
): PdfPage[] {
  const { w, h } = pageSize(false);
  const box = contentBox(w, h);
  // Two, not three. Three fitted more rows on a page and truncated what was
  // in them: across the ward's sheets it clipped 102 addresses and 304 of 768
  // names — "Ferguson, Nathan Darrell Griffith & Milena Toebe" lost 107pt of
  // itself. A roster exists to tell households apart, and unit numbers arrived
  // on it recently, so the column had to grow. At two, no address clips at all
  // and 18 names do.
  //
  // The cost is one page across the entire document: every sector sheet still
  // fits on one, and only the whole-ward roster goes from three pages to four.
  const cols = 2;
  const colW = (box.x1 - box.x0) / cols;
  const cellW = colW - ROSTER_GUTTER;
  const addrW = Math.min(
    Math.max(0, ...rows.map((r) => textWidth(rosterAddress(r), ROSTER_PT, true)))
      + ROSTER_GAP,
    cellW * 0.6,
  );
  // The head and the rule under it come out of every column's height, so the
  // rows below them start level.
  const headH = ROSTER_LEAD + 4;
  const perCol = Math.floor((box.y1 - box.y0 - headH) / ROSTER_LEAD);
  const perPage = perCol * cols;
  const pages: PdfPage[] = [];

  for (let start = 0; start < Math.max(rows.length, 1); start += perPage) {
    const page = doc.addPage(w, h);
    pages.push(page);
    const part = rows.slice(start, start + perPage);
    const of = rows.length > perPage
      ? ` — ${start + 1}–${start + part.length} of ${rows.length}`
      : '';
    furniture(page, title, `${rows.length} household${rows.length === 1 ? '' : 's'}${of}`);

    const blocks = Math.min(cols, Math.max(1, Math.ceil(part.length / perCol)));
    const headY = box.y1 - ROSTER_HEAD_PT;
    for (let c = 0; c < blocks; c++) {
      const cx = box.x0 + c * colW;
      page.text('Address', cx, headY, { size: ROSTER_HEAD_PT, gray: 0.45 });
      page.text('Household', cx + addrW, headY,
                { size: ROSTER_HEAD_PT, gray: 0.45 });
      page.strokeGray(0.6);
      page.lineWidth(0.5);
      page.dash(0, 0);
      page.segments([[{ x: cx, y: headY - 3 }, { x: cx + cellW, y: headY - 3 }]]);
    }

    const top = box.y1 - headH;
    part.forEach((r, i) => {
      const cx = box.x0 + Math.floor(i / perCol) * colW;
      const cy = top - ROSTER_PT - (i % perCol) * ROSTER_LEAD;
      page.text(clip(rosterAddress(r), addrW - ROSTER_GAP, ROSTER_PT, true), cx, cy,
                { size: ROSTER_PT, bold: true });
      if (r.label) {
        page.text(clip(r.label, cellW - addrW, ROSTER_PT), cx + addrW, cy,
                  { size: ROSTER_PT, gray: 0.2 });
      }
    });

    if (!rows.length) {
      page.text('No households in this sector.', box.x0, top - ROSTER_PT,
                { size: ROSTER_PT, gray: 0.4 });
    }
  }

  // Households the drawing cannot show, on the page that lists households
  // rather than under the map. They are real doors on this walk and the
  // walker still has to reach them; what the sheet cannot do is point at one.
  //
  // Its own page, and never squeezed onto the end of a column: a section that
  // matters precisely because it is easy to miss should not be the thing that
  // falls off the bottom.
  if (unmapped.length) {
    const groupCount = new Set(unmapped.map((r) => r.why)).size;
    // Heading, its one-line explanation, then per group a subhead and its
    // rows, plus the gap under each.
    const needed = ROSTER_LEAD * (2 + unmapped.length)
      + groupCount * (ROSTER_LEAD + 8) + 4;
    // Room left on the last roster page, measured under the deeper of its two
    // columns. A page of its own only when the section will not fit, so one
    // unplaceable household does not cost a sheet of paper.
    const onLast = pages.length && rows.length
      ? box.y1 - headH
        - Math.min(perCol, rows.length - (pages.length - 1) * perPage) * ROSTER_LEAD
        - box.y0
      : 0;
    let page: PdfPage;
    let cy: number;
    if (pages.length && onLast >= needed) {
      page = pages[pages.length - 1];
      cy = box.y0 + onLast - ROSTER_LEAD;
    } else {
      page = doc.addPage(w, h);
      pages.push(page);
      furniture(page, title, `${unmapped.length} not on the map`);
      cy = box.y1 - ROSTER_PT;
    }
    page.text('Not on the map', box.x0, cy, { size: ROSTER_PT + 1, bold: true });
    cy -= ROSTER_LEAD;
    page.text('Visit these; the drawing has no lot to point at.',
              box.x0, cy, { size: ROSTER_HEAD_PT, gray: 0.45 });
    cy -= ROSTER_LEAD + 2;

    const groups: [UnmappedRow['why'], string][] = [
      ['no-parcel', 'In the ward — no county parcel matches the address'],
      ['outside', 'Outside the ward boundary'],
    ];
    const addrW = Math.min(
      Math.max(0, ...unmapped.map((r) => textWidth(rosterAddress(r), ROSTER_PT, true)))
        + ROSTER_GAP,
      (box.x1 - box.x0) * 0.45,
    );
    for (const [why, heading] of groups) {
      const part = unmapped.filter((r) => r.why === why);
      if (!part.length) continue;
      page.text(heading, box.x0, cy, { size: ROSTER_HEAD_PT, gray: 0.45 });
      page.strokeGray(0.6);
      page.lineWidth(0.5);
      page.dash(0, 0);
      page.segments([[{ x: box.x0, y: cy - 3 }, { x: box.x1, y: cy - 3 }]]);
      cy -= ROSTER_LEAD + 2;
      for (const r of part) {
        page.text(clip(rosterAddress(r), addrW - ROSTER_GAP, ROSTER_PT, true),
                  box.x0, cy, { size: ROSTER_PT, bold: true });
        if (r.label) {
          page.text(clip(r.label, box.x1 - box.x0 - addrW, ROSTER_PT),
                    box.x0 + addrW, cy, { size: ROSTER_PT, gray: 0.2 });
        }
        cy -= ROSTER_LEAD;
      }
      cy -= 6;
    }
  }
  return pages;
}
